import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import List from '@mui/material/List'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { LuPlus } from 'react-icons/lu'
import IconNode from './IconNode'
import ListItemLink from './ListItemLink'
import DetachButton from './DetachButton'
import useNodeAttach from '../models/useNodeAttach'
import { useOrgRole } from '../hooks/useOrgRole'
import { formatNodeAddress } from '../utilities/general'
import { nodeNetworkLabel, type OutsideGroup } from '../utilities/nodeGroups'
import { nodeNameIn } from '../utilities/nodeNetworks'

/** What a node page says of a node in no network (app#498). */
const NO_NETWORK_LINE = 'Not in a network. This node runs no overlay. You reach it with commands and files.'

type Props = {
  node: NodeRecord
  /** The active org's networks. */
  networks: NetworkRecord[]
  group: OutsideGroup | undefined
  /** The reader controls the node. */
  canManage: boolean
}

/**
 * The Networks section of a node page (app#498): each network, the node's name and address there,
 * and Detach. **Attach to network** opens the one dialog.
 *
 * ⚠ Detach needs either side (PERMISSIONS.md § 5 Attachment): the node's controllers, or the
 * network's owner or an Admin. Attach needs the node's side here, and the dialog asks the network's.
 * ⚠ The app has no name for a network of another org, and that row has no link.
 */
export default function NodeNetworks({ node, networks, group, canManage }: Readonly<Props>) {
  const open = useNodeAttach(state => state.open)
  const { controls } = useOrgRole()

  return (
    <Box>
      <Typography variant="h6">Networks</Typography>
      {!node.networkIds.length && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {NO_NETWORK_LINE}
        </Typography>
      )}
      <List disablePadding>
        {node.networkIds.map(id => {
          const network = networks.find(n => n.id === id)
          const label = nodeNetworkLabel(network?.name, group)
          if (!network)
            return (
              <Typography key={id} variant="body2" sx={{ color: 'text.secondary' }}>
                {label}
              </Typography>
            )
          const name = nodeNameIn(node, id)
          return (
            <Stack key={id} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <ListItemLink to={`/networks/${id}`} icon={<IconNode type="network" size={20} />}>
                  {label}
                  <Typography variant="caption" color="textSecondary" component="div">
                    {name} · {formatNodeAddress({ name }, network)}
                  </Typography>
                </ListItemLink>
              </Box>
              {(canManage || controls(network.ownerId)) && (
                <DetachButton
                  network={label}
                  onClick={() => open({ mode: 'detach', nodeId: node.id, networkId: id })}
                />
              )}
            </Stack>
          )
        })}
      </List>
      {canManage && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<LuPlus />}
          sx={{ marginTop: 1 }}
          onClick={() => open({ mode: 'attach', nodeId: node.id })}
        >
          Attach to network
        </Button>
      )}
    </Box>
  )
}
